import React, { useState } from "react";
import { Grid, Box, Button } from "@mui/material";
import {
  asEntityId,
  formatDate,
  formatTime12Hour,
  hasPermission,
} from "../../../utils/commonutils";
import { addFollowup } from "../../../services/Follwupservices";
import { getApiErrorMessage, isApiSuccess } from "../../../utils/apiErrors";
import GlobalTimePicker from "../Timeinput";
import { MasterAutocomplete } from "../MasterSelect";
import {
  FALLBACK_FOLLOWUP_PRIORITY,
  FALLBACK_FOLLOWUP_STATUS,
} from "../../../utils/masterFallbacks";

const FollowupForm = ({ followupData, props, onSubmit }) => {
  const [searchText, setSearchText] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    date: "",
    time: "12:00 AM",
    note: "",
    priority: "",
    status: "",
    employee: { _id: asEntityId(followupData?.employeeId) },
    customer: { _id: asEntityId(followupData?.customerId) },
    taskId: followupData?._id,
  });

  const [errors, setErrors] = useState([]);
  const [submitError, setSubmitError] = useState("");
  const filteredFollowups = followupData?.followUps?.filter(
    (item) =>
      item.note?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.status?.toLowerCase().includes(searchText.toLowerCase()) ||
      item.priority?.toLowerCase().includes(searchText.toLowerCase()),
  );
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (val, field) => {
    setFormData((prev) => ({ ...prev, [field]: val }));
  };

  const handleSubmit = async () => {
    const requiredFields = [
      "title",
      "date",
      "time",
      "priority",
      "status",
      "note",
    ];
    const missing = requiredFields.filter((f) => !formData[f]);

    if (missing.length) {
      setErrors(missing);
      setSubmitError("");
      return;
    }
    setSubmitError("");
    // Read the owning task's ids at submit time so the follow-up is always linked to
    // the current company/employee even if the task loaded after this form mounted.
    const response = await addFollowup(props, {
      ...formData,
      employee: { _id: asEntityId(followupData?.employeeId) },
      customer: { _id: asEntityId(followupData?.customerId) },
      customerId: asEntityId(followupData?.customerId),
      employeeId: asEntityId(followupData?.employeeId),
      taskId: followupData?._id,
    });
    if (isApiSuccess(response)) {
      onSubmit();
      setFormData({
        title: "",
        date: "",
        time: "12:00 AM",
        note: "",
        priority: "",
        status: "",
        employee: { _id: asEntityId(followupData?.employeeId) },
        customer: { _id: asEntityId(followupData?.customerId) },
        taskId: followupData?._id,
      });
      setErrors([]);
      setSubmitError("");
    } else {
      setErrors(["note"]);
      setSubmitError(getApiErrorMessage(response, "Could not save follow-up"));
    }
  };

  return (
    <Box
      sx={{
        borderRadius: 3,
        boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
        border: "1px solid #f0f0f0",
        p: 3,
      }}
    >
      {/* ================= FORM ================= */}
      {hasPermission("create_followup") && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <input
              className="input-main"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Follow-up Title"
            />
            {errors.includes("title") && (
              <p className="error-message">Title is required</p>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <input
              className="input-main"
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
            />
            {errors.includes("date") && (
              <p className="error-message">Date is required</p>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <GlobalTimePicker
              value={
                formData.time
                  ? {
                      hour: formData.time.split(" ")[0]?.split(":")[0] || "12",
                      minute:
                        formData.time.split(" ")[0]?.split(":")[1] || "00",
                      ampm: formData.time.split(" ")[1] || "AM",
                    }
                  : { hour: "12", minute: "00", ampm: "AM" }
              }
              onChange={(val) => {
                setFormData((prev) => ({
                  ...prev,
                  time: val,
                }));
              }}
            />
            {errors.includes("time") && (
              <p className="error-message">Time is required</p>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <MasterAutocomplete
              group="followup_priority"
              fallback={FALLBACK_FOLLOWUP_PRIORITY}
              value={formData.priority}
              onChange={(e, val) => handleDropdownChange(val, "priority")}
              placeholder="Select Priority"
            />
            {errors.includes("priority") && (
              <p className="error-message">Priority is required</p>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <MasterAutocomplete
              group="followup_status"
              fallback={FALLBACK_FOLLOWUP_STATUS}
              value={formData.status}
              onChange={(e, val) => handleDropdownChange(val, "status")}
              placeholder="Select Status"
            />
            {errors.includes("status") && (
              <p className="error-message">Status is required</p>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <input
              className="input-main "
              rows={1}
              name="note"
              value={formData.note}
              onChange={handleChange}
              placeholder="Note"
            />
            {errors.includes("note") && (
              <p className="error-message">{submitError || "Note is required"}</p>
            )}
          </Grid>

          <Grid item xs={12} container justifyContent="flex-end">
            <Button variant="contained" onClick={handleSubmit}>
              Add Follow-up
            </Button>
          </Grid>
          {submitError && !errors.includes("note") ? (
            <Grid item xs={12}>
              <p className="error-message">{submitError}</p>
            </Grid>
          ) : null}
        </Grid>
      )}

      {/* ================= LIST ================= */}
      {followupData?.followUps?.length > 0 &&
        hasPermission("read_followup") && (
          <Box mt={3} mb={2}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={1}
              py={1}
            >
              <h5 style={{ margin: 0 }}>Follow-up List</h5>

              <input
                className="input-class"
                style={{ width: "250px" }}
                placeholder="Search follow-up..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Box>
            <table className="followup-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Note</th>
                </tr>
              </thead>

              <tbody className="followup-table-body">
                {filteredFollowups?.map((item, index) => (
                  <tr key={index}>
                    <td>{formatDate(item.date)}</td>
                    <td>{item.time}</td>
                    <td>
                      <span
                        className={`badge priority-${item.priority.toLowerCase()}`}
                      >
                        {item.priority}
                      </span>
                    </td>
                    <td>{item.status}</td>
                    <td>{item.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
    </Box>
  );
};

export default FollowupForm;
